import { useCallback, useEffect, useRef } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import {
	NeuroDivergentFontMultiplier,
	ZoomFactor,
} from "../state/accessibility";

import { PageSizeAtom } from "../state/page";

const PageSize = ({ containerSelector }: { containerSelector: string }) => {
	const setPageSizeAtom = useSetRecoilState(PageSizeAtom);
	const fontMultiplier = useRecoilValue(NeuroDivergentFontMultiplier);
	const setZoomFactor = useSetRecoilState(ZoomFactor);
	const initialDevicePixelRatio = useRef<number | null>(null);

	const calculateAndSetPageSize = useCallback(
		(viewerContainer: HTMLDivElement) => {
			let landscape = true;
			const height = viewerContainer.offsetHeight;
			const width = viewerContainer.offsetWidth;
			const containerX = viewerContainer.offsetLeft;
			const containerY = viewerContainer.offsetTop;

			if (height > width) {
				landscape = false;
			}

			if (width === 0 || height === 0) {
				return;
			}

			// https://math.stackexchange.com/questions/1620366/how-to-keep-aspect-ratio-and-position-of-rectangle-inside-another-rectangle
			const ratio = landscape ? 1.68 : 1.7778; // 1.68 is golden mean, 1.7778 is 16:9
			const h1 = landscape ? 1 : ratio;
			const h2 = height;
			const w1 = landscape ? ratio : 1;
			const w2 = width;

			const vertScale = h2 / h1;
			const horizScale = w2 / w1;

			const scale = Math.min(horizScale, vertScale);
			const pageHeight = Math.round(h1 * scale);
			const pageWidth = Math.round(w1 * scale);
			const y0 = (height - pageHeight) / 2;
			const x0 = (width - pageWidth) / 2;

			// Correctly calculate zoom level based on initial pixel ratio
			if (initialDevicePixelRatio.current === null) {
				initialDevicePixelRatio.current = window.devicePixelRatio || 1;
			}
			const currentRatio = window.devicePixelRatio || 1;
			const zoomLevel = currentRatio / initialDevicePixelRatio.current;

			// Update recoil state for other components that might need it
			setZoomFactor(zoomLevel);

			const fontSizeDenominator = landscape ? 100 : 60;

			// Raw base font size without font multiplier - used for padding/borders
			const baseFontSizeRaw =
				(Math.min(pageWidth, pageHeight) / fontSizeDenominator) * zoomLevel;

			const size = {
				baseFontSize: baseFontSizeRaw * fontMultiplier,
				baseFontSizeRaw,
				onePercentHeight: pageHeight / 100,
				onePercentWidth: pageWidth / 100,
				containerX,
				containerY,
				documentHeight: height,
				documentWidth: width,
				forceSingleColumn: fontMultiplier > 1, // WCAG 1.4.10: force single column when font enlarged
				gridXWidth: pageWidth / 100,
				gridYHeight: pageHeight / 100,
				height: pageHeight,
				landscape,
				width: pageWidth,
				x0,
				y0,
			};

			setPageSizeAtom(size);
		},
		[fontMultiplier, setPageSizeAtom, setZoomFactor],
	);

	useEffect(() => {
		const viewerContainer = document.getElementById(
			containerSelector,
		) as HTMLDivElement;
		if (!viewerContainer) {
			if (process.env.NODE_ENV !== "test")
				console.error("Incorrect container passed");
			return;
		}

		const handleResizeAndZoom = () => {
			calculateAndSetPageSize(viewerContainer);
		};

		const resizeObserver = new ResizeObserver(handleResizeAndZoom);
		resizeObserver.observe(viewerContainer);
		window.addEventListener("resize", handleResizeAndZoom);

		// Initial call
		handleResizeAndZoom();

		return () => {
			resizeObserver.disconnect();
			window.removeEventListener("resize", handleResizeAndZoom);
		};
	}, [containerSelector, calculateAndSetPageSize]);

	return <></>;
};

export default PageSize;
